import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config';
import { tokenManager } from '../../utils/tokenManager';
import { PaginationParams, PaginatedResponse, ReportData, FileInfo, FileBasedReport, ReportParams, FileListResponse } from '../../types/report.types';
import { RootState } from '../store';
import { ActionTypes } from '../reducers/reportReducer';
import { AppDispatch } from '../store';

// Create axios instance
const api = axios.create({
    baseURL: config.API_BASE_URL,
    withCredentials: true,
});

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

const getTokenWithRetry = async (retries = 0): Promise<string | null> => {
    try {
        const token = await tokenManager.getToken();
        if (token) {
            return token;
        }
        if (retries < MAX_RETRIES) {
            console.log(`Token not found, retrying in ${RETRY_DELAY}ms...`);
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
            return getTokenWithRetry(retries + 1);
        }
        console.log('Max retries reached, no token found');
        return null;
    } catch (error) {
        console.error('Error in getTokenWithRetry:', error);
        return null;
    }
};

// Add interceptors
api.interceptors.request.use(
    async (config) => {
        const token = await getTokenWithRetry();
        const authType = tokenManager.getAuthType();
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        if (authType) {
            config.headers['X-Auth-Type'] = authType.toLowerCase();
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    response => response,
    error => Promise.reject(error)
);

const handleApiError = (error: any) => {
    if (error.response) {
        throw new Error(error.response.data.message || 'API Error');
    }
    throw error;
};

export const CLEAR_REPORT_MESSAGES = 'CLEAR_REPORT_MESSAGES';

export const clearReportMessages = () => ({
    type: CLEAR_REPORT_MESSAGES
});

export const setLoading = (loading: boolean) => ({
    type: ActionTypes.SET_LOADING,
    payload: loading
});

export const setError = (error: string | null) => ({
    type: ActionTypes.SET_ERROR,
    payload: error
});

export const fetchDailyReport = createAsyncThunk<
    PaginatedResponse<ReportData>,
    ReportParams,
    { state: RootState }
>('reports/fetchDaily', async (params) => {
    try {
        const { year, month, day, page, per_page, sort_field, sort_direction, signal } = params;
        const response = await api.post('/reports/daily', {
            year,
            month,
            day,
            pagination: { 
                page, 
                per_page,
                sort_field,
                sort_direction
            }
        }, { signal });
        return response.data;
    } catch (error: any) {
        if (error?.name === 'AbortError') {
            throw new Error('Request aborted');
        }
        return handleApiError(error);
    }
});

export const fetchWeeklyReport = createAsyncThunk<
    PaginatedResponse<ReportData>,
    ReportParams,
    { state: RootState }
>('reports/fetchWeekly', async (params) => {
    try {
        const { year, week, page, per_page, sort_field, sort_direction, signal } = params;
        const response = await api.post('/reports/weekly', {
            year,
            week,
            pagination: { 
                page, 
                per_page,
                sort_field,
                sort_direction
            }
        }, { signal });
        return response.data;
    } catch (error: any) {
        if (error?.name === 'AbortError') {
            throw new Error('Request aborted');
        }
        return handleApiError(error);
    }
});

export const fetchMonthlyReport = createAsyncThunk<
    PaginatedResponse<ReportData>,
    ReportParams,
    { state: RootState }
>('reports/fetchMonthly', async (params) => {
    try {
        const { year, month, page, per_page, sort_field, sort_direction, signal } = params;
        const response = await api.post('/reports/monthly', {
            year,
            month,
            pagination: { 
                page, 
                per_page,
                sort_field,
                sort_direction
            }
        }, { signal });
        return response.data;
    } catch (error: any) {
        if (error?.name === 'AbortError') {
            throw new Error('Request aborted');
        }
        return handleApiError(error);
    }
});

export const fetchFileBasedReport = createAsyncThunk<
    PaginatedResponse<ReportData>,
    ReportParams,
    { state: RootState }
>('reports/fetchFileBased', async (params) => {
    try {
        const { fileId, year, month, day, page, per_page, sort_field, sort_direction, signal } = params;
        console.log('Fetching file-based report with params:', { fileId, year, month, day, page, per_page, sort_field, sort_direction });
        
        const response = await api.get(`/reports/file-based/${fileId}`, {
            params: {
                year,
                month,
                day,
                page,
                per_page,
                sort_field,
                sort_direction
            },
            signal
        });
        
        console.log('File-based report response:', response.data);
        return response.data;
    } catch (error: any) {
        console.error('Error fetching file-based report:', error);
        if (error?.name === 'AbortError') {
            throw new Error('Request aborted');
        }
        throw error; // Let the error middleware handle it
    }
});

// New file-based actions
export const fetchFilesForWeek = createAsyncThunk<
    FileListResponse,
    { start_date: string; end_date: string },
    { state: RootState }
>('reports/fetchFilesForWeek', async (params) => {
    try {
        const { start_date, end_date } = params;
        
        // Optional: Client-side validation (if it exists)
        const startDate = new Date(start_date);
        const endDate = new Date(end_date);
        const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays > 30) {  // Changed from 6 to 30
            throw new Error("Date range cannot exceed 31 days");
        }
        
        const response = await api.get('/reports/files/week', {
            params: {
                start_date,
                end_date
            }
        });
        return response.data;
    } catch (error) {
        return handleApiError(error);
    }
});

export const selectFile = (file: FileInfo | null) => ({
    type: ActionTypes.SELECT_FILE,
    payload: file
});

export const getFileBasedReport = (fileId: number, year: number, month: number, day: number, page: number = 1, per_page: number = 10) => async (dispatch: AppDispatch) => {
    try {
        dispatch(setLoading(true));
        const response = await api.get(`/api/reports/file-based/${fileId}`, {
            params: {
                year,
                month,
                day,
                page,
                per_page
            }
        });
        
        dispatch({
            type: ActionTypes.SET_REPORT_DATA,
            payload: response.data
        });
        
        // Update pagination if available
        if (response.data.metadata) {
            dispatch({
                type: ActionTypes.SET_PAGINATION,
                payload: {
                    page: response.data.metadata.page,
                    per_page: response.data.metadata.per_page,
                    total_items: response.data.metadata.total_records,
                    total_pages: response.data.metadata.total_pages
                }
            });
        }
    } catch (error) {
        dispatch(setError('Error fetching file-based report'));
        console.error('Error fetching file-based report:', error);
    } finally {
        dispatch(setLoading(false));
    }
};

export const downloadDailyReport = createAsyncThunk(
    'reports/downloadDaily',
    async (params: { year: string, month: string, day: string }) => {
        try {
            const response = await api.get('/reports/daily/download', {
                params,
                responseType: 'blob'
            });
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `daily_report_${params.year}-${params.month}-${params.day}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            return response.data;
        } catch (error: any) {
            console.error('Error downloading daily report:', error);
            throw error;
        }
    }
);

export const downloadWeeklyReport = createAsyncThunk(
    'reports/downloadWeekly',
    async (params: { year: string, week: string }) => {
        try {
            const response = await api.get('/reports/weekly/download', {
                params,
                responseType: 'blob'
            });
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `weekly_report_${params.year}-W${params.week}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            return response.data;
        } catch (error: any) {
            console.error('Error downloading weekly report:', error);
            throw error;
        }
    }
);

export const downloadMonthlyReport = createAsyncThunk(
    'reports/downloadMonthly',
    async (params: { year: string, month: string }) => {
        try {
            const response = await api.get('/reports/monthly/download', {
                params,
                responseType: 'blob'
            });
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `monthly_report_${params.year}-${params.month}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            return response.data;
        } catch (error: any) {
            console.error('Error downloading monthly report:', error);
            throw error;
        }
    }
);

export const downloadFileBasedReport = createAsyncThunk(
    'reports/downloadFileBased',
    async (params: { fileId: string, year: string, month: string, day: string, fileName: string }) => {
        try {
            const { fileId, ...queryParams } = params;
            const response = await api.get(`/reports/file-based/${fileId}/download`, {
                params: queryParams,
                responseType: 'blob'
            });
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const baseFileName = params.fileName.split('.')[0];
            link.setAttribute('download', `${baseFileName}_report.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            return response.data;
        } catch (error: any) {
            console.error('Error downloading file-based report:', error);
            throw error;
        }
    }
);